import { Dispatch, SetStateAction, useState } from "react";
import { AlertCircle, Plus } from "lucide-react";
import { ScrollRestoration } from "react-router-dom";
import Modal from "@/components/ui/Modal";
import AddProductModal from "./components/AddProductModal";
import { useAppSelector } from "@/redux/hooks/hooks";
import VideoAnalysisModal from "./components/videoAnalysisModal/VideoAnalysisModal";
import { useGetBrandDetailsQuery } from "@/redux/feature/videoDashboard/VideoDashboardApi";
import BrandDetailsModal from "./components/BrandDetailsModal";
import { useGetBrandSummaryQuery } from "@/redux/feature/brandCart/brandCartApi";
import BrandCartTable from "./components/BrandCartTable";

const VideoDashboard = () => {
  const [isBrandDetailsModalOpen, setIsBrandDetailsModalOpen] = useState(false);
  const [isVideoAnalysisModalOpen, setIsVideoAnalysisModalOpen] =
    useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const { selectedTeam } = useAppSelector((state) => state.global);

  // brand details
  const { user } = useAppSelector((state) => state.auth);
  const { data: brandDetails, isLoading: isBrandDetailsLoading } =
    useGetBrandDetailsQuery(user?.email);

  const { data: cartData, isLoading: isCartDataLoading } =
    useGetBrandSummaryQuery(
      {
        brand: brandDetails?.brand,
      },
      {
        skip: !brandDetails?.hasDetails,
      }
    );

  return (
    <div>
      <div className="flex flex-col md:flex-row items-start justify-between mb-8 mt-5 md:mt-0">
        <div className="space-y-2 flex-1">
          <h1 className="text-gray-900 text-2xl font-semibold">
            {selectedTeam?._id
              ? selectedTeam.name
              : "Video Marketing Dashboard"}
          </h1>
          <p className="text-gray-500 text-sm">
            Manage and enhance your marketing videos
          </p>
        </div>

        <div className="flex justify-between gap-x-4 items-center w-full md:w-fit mt-3 md:mt-0">
          {!selectedTeam?._id && (
            <button
              onClick={() => setIsVideoAnalysisModalOpen(true)}
              className="text-sm py-4 px-4 rounded-lg transition-all font-medium text-gray-600 sm:bg-transparent hover:bg-gray-50 border border-gray-200"
            >
              Video analysis
            </button>
          )}

          <button
            onClick={() => setIsBrandDetailsModalOpen(true)}
            className="flex items-center gap-3 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg border border-gray-200 transition-all duration-200"
          >
            <div className="w-8 h-8 bg-[#FF6600] rounded-full flex items-center justify-center">
              <span className="text-white text-xs font-bold">AI</span>
            </div>
            <div className="text-left hidden sm:block">
              <div className="font-medium text-gray-900">AI Marketplace</div>
              <div className="text-xs text-gray-500">View profile</div>
            </div>
            <svg
              className="w-4 h-4 text-gray-400 hidden sm:block"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* brand cart */}
      <div className="mb-10">
        {brandDetails?.hasDetails ? (
          <BrandCartTable
            brandData={cartData?.data}
            isLoading={isCartDataLoading}
          />
        ) : (
          <BrandDetailsPrompt
            setIsBrandDetailsModalOpen={setIsBrandDetailsModalOpen}
          />
        )}
      </div>

      <ScrollRestoration />

      <Modal
        isOpen={isBrandDetailsModalOpen}
        setOpen={setIsBrandDetailsModalOpen}
        width="500px"
      >
        <BrandDetailsModal setIsOpen={setIsBrandDetailsModalOpen} />
      </Modal>

      <Modal
        isOpen={isVideoAnalysisModalOpen}
        setOpen={setIsVideoAnalysisModalOpen}
        width="800px"
      >
        <VideoAnalysisModal setIsOpen={setIsVideoAnalysisModalOpen} />
      </Modal>

      <Modal
        isOpen={isOpen}
        setOpen={setIsOpen}
        width="500px"
        preventCloseOnClickOutside={true}
      >
        <AddProductModal setIsOpen={setIsOpen} />
      </Modal>
    </div>
  );
};

export default VideoDashboard;

const BrandDetailsPrompt = ({
  setIsBrandDetailsModalOpen,
}: {
  setIsBrandDetailsModalOpen: Dispatch<SetStateAction<boolean>>;
}) => {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
      <div className="mb-6">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <AlertCircle className="w-8 h-8 text-gray-400" />
        </div>
      </div>

      <h3 className="text-lg font-medium text-gray-900 mb-2">
        Brand Details Required
      </h3>

      <p className="text-gray-500 mb-8 max-w-sm">
        Please add your brand details first to view and manage your cart items.
      </p>

      <button
        onClick={() => setIsBrandDetailsModalOpen(true)}
        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
      >
        <Plus className="w-4 h-4 mr-2" />
        Add Brand Details
      </button>
    </div>
  );
};
